import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check, ChevronRight, Copy } from 'lucide-react'
import { useReveal } from '../../../ui/Cord'
import { useToast } from '../../../ui'

/* ===========================================================================
   Piezas compartidas por las secciones de la página pública.

   La página del invitado es callada a propósito: una sola columna, alineada a
   la izquierda, reglas finas, mucho aire. Toda la audacia se gasta en el
   cordón que la recorre por el borde izquierdo.
   =========================================================================== */

export function cx(...partes: Array<string | false | null | undefined>): string {
  return partes.filter(Boolean).join(' ')
}

/**
 * Solo http(s). La configuración la escribe la pareja, pero un enlace
 * `javascript:` guardado por error no debe llegar nunca al invitado.
 * Acepta rutas relativas (`/f/...`) porque así vienen las fotos subidas.
 */
export function urlSegura(bruto: string | null | undefined): string | null {
  const texto = (bruto ?? '').trim()
  if (texto === '') return null
  try {
    const url = new URL(texto, window.location.origin)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/** Un color de paleta solo entra a un `style` si es un hex de verdad. */
export function colorSeguro(bruto: string | null | undefined): string | null {
  const texto = (bruto ?? '').trim()
  if (!HEX.test(texto)) return null
  return texto.startsWith('#') ? texto.toLowerCase() : `#${texto.toLowerCase()}`
}

export function tieneTexto(valor: string | null | undefined): boolean {
  return (valor ?? '').trim() !== ''
}

// ---------------------------------------------------------------------------
// Rastro de confirmación
// ---------------------------------------------------------------------------

/** Clave que deja la pantalla de confirmación al terminar el RSVP. */
export function claveDeConfirmacion(slug: string): string {
  return `nuestra-boda.rsvp.${slug}`
}

/** Si el invitado ya confirmó en este navegador, el botón flotante sobra. */
export function yaConfirmo(slug: string): boolean {
  try {
    return localStorage.getItem(claveDeConfirmacion(slug)) !== null
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Botones y enlaces
// ---------------------------------------------------------------------------

/** Alto cómodo para el pulgar: 46px, no los 40px del panel. */
const BOTON =
  'inline-flex min-h-[46px] items-center justify-center gap-2 rounded-md px-5 py-3 ' +
  'text-sm font-medium transition-colors'

export const BOTON_CLARO = `${BOTON} bg-tinta text-bruma hover:bg-tinta-suave`
export const BOTON_CONTORNO = `${BOTON} border border-palo text-tinta hover:bg-palo/40`
export const BOTON_SOBRE_OSCURO = `${BOTON} bg-bruma text-tinta hover:bg-palo`
export const BOTON_CONTORNO_OSCURO = `${BOTON} border border-palo/45 text-bruma hover:bg-bruma/10`

/** Enlace externo: siempre en pestaña nueva y sin regalar el referrer. */
export function EnlaceExterno({
  href,
  className,
  children,
}: {
  href: string
  className?: string
  children: ReactNode
}) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  )
}

export function BotonCopiar({
  texto,
  etiqueta = 'Copiar',
  sobreOscuro = false,
  className,
}: {
  texto: string
  etiqueta?: string
  /** Sobre fondo tinta el contorno claro no se ve; se cambia entero. */
  sobreOscuro?: boolean
  className?: string
}) {
  const toast = useToast()
  const [copiado, setCopiado] = useState(false)

  async function copiar() {
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      toast.ok('Copiado')
      window.setTimeout(() => setCopiado(false), 2500)
    } catch {
      toast.error('Tu navegador no nos deja copiar. Mantén pulsado el texto y cópialo a mano.')
    }
  }

  return (
    <button
      type="button"
      onClick={copiar}
      className={cx(sobreOscuro ? BOTON_CONTORNO_OSCURO : BOTON_CONTORNO, className)}
    >
      {copiado ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
      {copiado ? 'Copiado' : etiqueta}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Envoltura de sección
// ---------------------------------------------------------------------------

type Tono = 'bruma' | 'palo' | 'tinta'

const FONDOS: Record<Tono, string> = {
  bruma: 'bg-bruma text-tinta',
  palo: 'bg-palo text-tinta',
  tinta: 'bg-tinta text-bruma',
}

export interface SeccionProps {
  /** Ancla y destino de los enlaces internos. */
  id: string
  /** Etiqueta versalita. Nunca un número 01/02/03. */
  eyebrow: string
  titulo?: string
  tono?: Tono
  /** 'texto' para leer de corrido; 'ancho' para rejillas de fotos. */
  ancho?: 'texto' | 'ancho'
  children: ReactNode
}

/**
 * Cada sección visible es un nudo del cordón: `data-nudo` es lo que la página
 * mide para colocarlos.
 */
export function Seccion({
  id,
  eyebrow,
  titulo,
  tono = 'bruma',
  ancho = 'texto',
  children,
}: SeccionProps) {
  const ref = useReveal<HTMLElement>()

  /* En escritorio, las secciones de texto adoptan una rejilla editorial:
     cabecera (eyebrow + título) en una columna angosta a la izquierda y el
     contenido a su derecha. La rejilla entera va CENTRADA en el viewport: en
     una pantalla ancha el aire sobrante se reparte simétrico a ambos lados
     —espacio en blanco deliberado, no un costado muerto—, que es lo que
     distingue una composición editorial de una página a medio llenar. */
  const editorial = ancho === 'texto'

  return (
    <section
      id={id}
      ref={ref}
      data-nudo=""
      aria-labelledby={`${id}-titulo`}
      className={cx(
        // La costura entre dos secciones del mismo tono queda en ~4rem en el
        // teléfono y ~6rem desde tablet: aire editorial, no hueco. Las bandas
        // de tono (versículo, hashtag) son las que marcan las pausas grandes.
        //
        // El padding móvil bajó de py-10 a py-8 porque aquí se cobra siete
        // veces: en una pantalla de 360 px esos 16 px por sección eran 112 px
        // de la página. En tablet y escritorio, donde el alto no aprieta, el
        // ritmo original se mantiene intacto.
        'revelar px-5 py-8 sm:px-10 sm:py-12 lg:px-16 lg:py-12',
        FONDOS[tono],
      )}
    >
      <div className={editorial ? 'mx-auto max-w-[66rem]' : 'mx-auto max-w-6xl'}>
        {/* El filete es el remate del eyebrow, no un separador aparte: pegado
            a lo que introduce se lee mejor y devuelve 8 px por sección. */}
        <hr className={cx('regla mb-3 w-16', tono === 'tinta' && 'border-t-palo/30')} />
        <div
          className={cx(
            editorial && 'lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-x-10',
          )}
        >
          <div>
            <p className={cx('eyebrow', tono === 'tinta' ? 'text-palo' : 'text-palo-hondo')}>
              {eyebrow}
            </p>
            <h2
              id={`${id}-titulo`}
              className={cx(
                'mt-2 font-display text-titulo',
                titulo ? '' : 'sr-only',
                tono === 'tinta' ? 'text-bruma' : 'text-tinta',
              )}
            >
              {titulo ?? eyebrow}
            </h2>
          </div>
          {/* El filete ancla el contenido a su título: la columna izquierda se
              lee como nota al margen de una revista, no como espacio sobrante. */}
          <div
            className={cx(
              // El título y su contenido son una unidad: 16 px los separan de
              // sobra y el aire grande queda para la costura entre secciones.
              'mt-4 sm:mt-6',
              editorial && 'lg:mt-1 lg:max-w-[48rem] lg:border-l lg:pl-10',
              editorial && (tono === 'tinta' ? 'lg:border-palo/25' : 'lg:border-palo/70'),
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Fotos
// ---------------------------------------------------------------------------

/**
 * Texto alternativo de una foto: su pie si lo tiene, si no quién la subió.
 *
 * Vive aquí y no en cada pantalla porque estaba copiado literalmente en la
 * galería y en el visor, y el sufijo `?tam=mini` de abajo es un contrato con
 * el Worker: dos copias podían divergir sin que nada avisara.
 */
export function textoAlternativo(foto: { caption: string | null; uploaderName: string | null }): string {
  if (tieneTexto(foto.caption)) return foto.caption ?? ''
  if (tieneTexto(foto.uploaderName)) return `Foto compartida por ${foto.uploaderName}`
  return 'Foto de la boda'
}

/** Versión reducida para las rejillas. El visor y la descarga piden el original. */
export function miniatura(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}tam=mini`
}

// ---------------------------------------------------------------------------
// Acceso a una pantalla aparte
// ---------------------------------------------------------------------------

/**
 * Una sección que no despliega su contenido: lo anuncia y lleva a él.
 *
 * El programa y la galería medían 1445 y 1282 px dentro de la invitación —2727
 * de 6870, más de un tercio de la página— y había que recorrerlos enteros para
 * llegar a lo de abajo. Ahora cada uno vive en su pantalla y aquí queda una
 * fila que cabe de un vistazo: qué es, cuánto hay, y una galoneta.
 *
 * Sigue siendo `<section data-nudo>` porque el cordón cuenta sus nudos por
 * secciones visibles: si esto fuera un `<div>`, la trenza perdería dos.
 */
export function AccesoAPantalla({
  id,
  eyebrow,
  titulo,
  resumen,
  a,
  icono,
}: {
  id: string
  eyebrow: string
  titulo: string
  /** Una línea con una cifra real: «18 momentos», «11 fotos». */
  resumen: string
  a: string
  icono: ReactNode
}) {
  const ref = useReveal<HTMLElement>()

  return (
    <section
      id={id}
      ref={ref}
      data-nudo=""
      aria-labelledby={`${id}-titulo`}
      className="revelar bg-bruma px-5 py-4 text-tinta sm:px-10 sm:py-5 lg:px-16"
    >
      <div className="mx-auto max-w-[66rem]">
        <Link
          to={a}
          className="group flex items-center gap-4 border-y border-palo/70 py-4 transition-colors hover:bg-palo/20"
        >
          <span aria-hidden="true" className="shrink-0 text-laton">
            {icono}
          </span>
          <span className="min-w-0 flex-1">
            <span className="eyebrow block">{eyebrow}</span>
            <span
              id={`${id}-titulo`}
              className="mt-0.5 block font-display text-xl text-tinta sm:text-2xl"
            >
              {titulo}
            </span>
            <span className="mt-0.5 block text-sm text-palo-hondo">{resumen}</span>
          </span>
          <ChevronRight
            size={20}
            aria-hidden="true"
            className="shrink-0 text-palo-hondo transition-transform group-hover:translate-x-0.5"
          />
        </Link>
      </div>
    </section>
  )
}
